// Risk Results Dashboard — the "Detail" view rendered post-calculation.
//
// Three blocks, in priority order:
//
//   1. Headline strip      -- profile name + four 0-100 score pills
//                              (Tolerance / Capacity / Behavioral /
//                              Total) and the active/passive style
//                              score. Anchored at the top so the user
//                              immediately sees their bottom line.
//   2. Persona Card        -- branded block surfacing the persona the
//                              backend matched ("The Market Rider"
//                              etc.) with quadrant + tilt + colour.
//   3. Two-up charts       -- behavioral RADAR (Emotional vs.
//                              Behavioral) on the left, allocation
//                              DONUT on the right.
//
// The bottom of the page hosts the "Generate Report" CTA which posts
// the user's raw answers to /assessment/report/download and triggers
// a browser download via an object URL. The endpoint itself reruns
// the scoring pipeline server-side so the PDF is guaranteed to match
// the on-screen numbers.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../../../shared/ui';
import {
    riskApi,
    type CalculateProfileResponse,
    type RawAnswers,
} from '../api/riskApi';
import { AllocationDonut } from './AllocationDonut';
import { RiskRadarChart, insightsToRadarPoints } from './RiskRadarChart';

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------

const ScorePill = ({
    label,
    value,
    suffix = '',
}: {
    label: string;
    value: number | string;
    suffix?: string;
}) => (
    <div className="flex flex-col items-start rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 px-4 py-3">
        <span className="text-[11px] uppercase tracking-wide muted">
            {label}
        </span>
        <span className="text-2xl font-black tabular-nums text-slate-900 dark:text-white">
            {typeof value === 'number' ? value.toFixed(1) : value}
            {suffix && (
                <span className="ml-0.5 text-sm font-bold muted">{suffix}</span>
            )}
        </span>
    </div>
);

// Tiny inline spinner — keeps the bundle dependency-free, matches the
// emerald accent the rest of the feature uses. Sized to inherit from
// font-size so it sits cleanly inside a button.
const Spinner = ({ className = '' }: { className?: string }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden
        className={`h-4 w-4 animate-spin ${className}`}
    >
        <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeOpacity="0.25"
            strokeWidth="3"
        />
        <path
            d="M22 12a10 10 0 0 1-10 10"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
        />
    </svg>
);

// ---------------------------------------------------------------------------
// Persona Card
// ---------------------------------------------------------------------------

/** Type-narrowed view of the backend persona dict. Every field is
 *  optional because legacy persisted rows may pre-date the richer
 *  schema; we always render *something* even if only ``name`` is
 *  present. */
interface PersonaShape {
    name?: string;
    quadrant?: string;
    description?: string;
    tilt?: string;
    color?: string;
    bg?: string;
    icon?: string;
}

const PersonaCard = ({ persona }: { persona: Record<string, unknown> }) => {
    // Persona arrives as `Record<string, unknown>` over the wire so we
    // narrow it here -- safer than declaring `any` upstream.
    const p = persona as PersonaShape;

    const accent = p.color ?? '#10b981';
    const bg = p.bg ?? 'rgba(16,185,129,0.07)';

    return (
        <section
            className="surface-card relative overflow-hidden"
            aria-label={p.name ? `Persona: ${p.name}` : 'Persona'}
            style={{ borderLeft: `4px solid ${accent}` }}
        >
            {/* Tinted backdrop -- driven by the persona's brand colour
                so the four personas (Venture Investor / Market Rider /
                Income Specialist / Safety First) feel visually
                distinct without us hard-coding each one. */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{ background: bg }}
            />
            <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start">
                {p.icon && (
                    <div
                        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-3xl shadow-sm"
                        style={{
                            background: 'rgba(255,255,255,0.85)',
                            color: accent,
                        }}
                    >
                        <span aria-hidden>{p.icon}</span>
                    </div>
                )}

                <div className="flex-1">
                    <span className="text-[11px] font-bold uppercase tracking-wider muted">
                        Your investor persona
                    </span>
                    <h3
                        className="mt-1 text-2xl font-black tracking-tight text-slate-900 dark:text-white"
                    >
                        {p.name ?? 'Persona'}
                    </h3>

                    {p.quadrant && (
                        <span
                            className="mt-2 inline-flex items-center rounded-full px-2.5 py-1 text-xs font-extrabold uppercase tracking-wider"
                            style={{
                                background: 'rgba(255,255,255,0.85)',
                                color: accent,
                            }}
                        >
                            {p.quadrant}
                        </span>
                    )}

                    {p.description && (
                        <p className="mt-3 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                            {p.description}
                        </p>
                    )}

                    {p.tilt && (
                        <p className="mt-3 text-sm font-semibold text-slate-800 dark:text-slate-100">
                            <span className="muted font-semibold">Allocation tilt: </span>
                            {p.tilt}
                        </p>
                    )}
                </div>
            </div>
        </section>
    );
};

// ---------------------------------------------------------------------------
// Generate Report CTA
// ---------------------------------------------------------------------------

type DownloadStatus =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'error'; message: string };

interface GenerateReportButtonProps {
    /** The 43-answer payload fed to /assessment/report/download. */
    rawAnswers: RawAnswers | null;
    /** Filename suggestion for the saved PDF. */
    filename?: string;
}

const GenerateReportButton = ({
    rawAnswers,
    filename = 'forge-ultra-risk-report.pdf',
}: GenerateReportButtonProps) => {
    const [status, setStatus] = useState<DownloadStatus>({ kind: 'idle' });

    const disabled = status.kind === 'loading' || rawAnswers === null;

    const handleClick = async () => {
        if (rawAnswers === null) return;
        setStatus({ kind: 'loading' });
        try {
            const blob = await riskApi.downloadReport(rawAnswers);
            // ``createObjectURL`` -> hidden <a> -> click is the only
            // way to start a download from XHR/axios responses across
            // browsers. We revoke the URL on the next tick to free
            // the blob; modern browsers also revoke automatically on
            // navigation, but explicit > implicit.
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
            setStatus({ kind: 'idle' });
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[GenerateReportButton] /report/download failed', err);
            setStatus({
                kind: 'error',
                message:
                    'We could not generate your report. Please try again in a moment.',
            });
        }
    };

    return (
        <div className="flex flex-col gap-2">
            <Button
                variant="primary"
                size="md"
                onClick={handleClick}
                disabled={disabled}
                aria-busy={status.kind === 'loading'}
            >
                {status.kind === 'loading' ? (
                    <>
                        <Spinner className="mr-2" />
                        Generating report…
                    </>
                ) : (
                    'Generate PDF Report'
                )}
            </Button>
            {rawAnswers === null && (
                <p className="text-xs muted">
                    Retake the assessment to refresh the data needed for the PDF.
                </p>
            )}
            {status.kind === 'error' && (
                <p className="text-xs font-semibold text-red-600 dark:text-red-400">
                    {status.message}
                </p>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main dashboard
// ---------------------------------------------------------------------------

export interface RiskResultsDashboardProps {
    profile: CalculateProfileResponse;
    /**
     * The verbatim 43-answer payload to forward to
     * /report/download. ``null`` when the dashboard is rendered from a
     * persisted row that pre-dates ``raw_answers`` -- the Generate
     * Report button is disabled in that case with a hint to retake.
     */
    rawAnswers: RawAnswers | null;
    onRetake: () => void;
}

export const RiskResultsDashboard = ({
    profile,
    rawAnswers,
    onRetake,
}: RiskResultsDashboardProps) => {
    const radarPoints = useMemo(
        () => insightsToRadarPoints(profile.insights),
        [profile.insights],
    );

    return (
        <div className="space-y-6 animate-fade-slide-up">
            {/* Headline strip — profile + four scores. ----------------- */}
            <section className="surface-card">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <div>
                        <h2 className="page-title">{profile.risk_profile}</h2>
                        <p className="page-subtitle">
                            Your composite risk profile, derived from{' '}
                            <span className="font-semibold text-slate-900 dark:text-slate-100">
                                {profile.user || 'your responses'}
                            </span>
                            .
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button variant="secondary" size="sm" onClick={onRetake}>
                            Retake assessment
                        </Button>
                        <Link
                            to="/dashboard/risk-assessment"
                            className="inline-flex items-center justify-center rounded-xl border border-slate-300 dark:border-slate-700 px-4 py-2 text-sm font-bold text-slate-700 dark:text-slate-200 hover:border-emerald-400 dark:hover:border-emerald-400 transition-colors"
                        >
                            Back to suite
                        </Link>
                    </div>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <ScorePill
                        label="Tolerance"
                        value={profile.risk_scores.tolerance}
                        suffix="/100"
                    />
                    <ScorePill
                        label="Capacity"
                        value={profile.risk_scores.capacity}
                        suffix="/100"
                    />
                    <ScorePill
                        label="Behavioral"
                        value={profile.risk_scores.behavioral}
                        suffix="/100"
                    />
                    <ScorePill
                        label="Total"
                        value={profile.risk_scores.total}
                        suffix="/100"
                    />
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                    <ScorePill
                        label="Style score"
                        value={profile.style_score}
                        suffix="/100"
                    />
                    <p className="flex-1 min-w-[14rem] rounded-xl bg-slate-100 dark:bg-slate-800/60 px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                        Higher = more active. Lower = more passive. Drives the{' '}
                        <em>active / passive tilt</em> applied to your model
                        portfolio.
                    </p>
                </div>
            </section>

            {/* Persona card -------------------------------------------- */}
            <PersonaCard persona={profile.persona} />

            {/* Two-up: behavioral radar + allocation donut. ------------ */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <section className="surface-card">
                    <h3 className="section-title mb-4">Behavioral insights</h3>
                    <p className="text-sm muted mb-4">
                        Your bias profile across the two cognitive buckets.
                        Lower is better — high scores flag the dimensions
                        most likely to derail long-horizon compounding.
                    </p>
                    <RiskRadarChart points={radarPoints} />
                </section>
                <section className="surface-card">
                    <h3 className="section-title mb-4">
                        Recommended allocation
                    </h3>
                    <p className="text-sm muted mb-4">
                        Asset-class weights derived from your scores and
                        style. Hover a slice to see the exact percentage.
                    </p>
                    <AllocationDonut
                        allocation={profile.portfolio.allocation}
                    />
                </section>
            </div>

            {/* Generate Report CTA ------------------------------------- */}
            <section className="surface-card">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h3 className="section-title">Take it with you</h3>
                        <p className="mt-1 text-sm muted max-w-xl">
                            Download the full institutional-grade PDF —
                            scoring breakdown, behavioral radar, persona
                            narrative and recommended allocation, branded
                            and ready to share with your advisor.
                        </p>
                    </div>
                    <GenerateReportButton
                        rawAnswers={rawAnswers}
                        filename={`forge-ultra-risk-report-${
                            profile.risk_profile?.replace(/\s+/g, '-').toLowerCase() ||
                            'profile'
                        }.pdf`}
                    />
                </div>
            </section>
        </div>
    );
};

export default RiskResultsDashboard;
